import { useState, useEffect, useRef } from 'react';
import schema from '../configuration/characteristics_schema';
import Documents from '../components/Documents';
import Characteristics from '../components/Characteristics';
import EstimateCharacteristicsFromAddress from '../components/EstimateCharacteristicsFromAddress';
import Meldings from '../components/Meldings';
import Cookies from 'js-cookie';
import { Characteristic, Schema, Document } from '../types';
import { ToastContainer, toast } from 'react-toastify';

const typedSchema = schema as Schema;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.thoughtful-oasis.com';

const Home = () => {
    const [editableCharacteristics, setEditableCharacteristics] = useState<Record<string, Characteristic>>({});
    const [estimatedCharacteristicsReport, setEstimatedCharacteristicsReport] = useState<{
        characteristics: Record<string, {
            answer: string;
            explanation: string;
            confidence: number;
        }>;
        report: string;
    } | null>(null);
  

    const [selectedMeldings, setSelectedMeldings] = useState<string[]>([]);
    const [estimationLoading, setEstimationLoading] = useState(false);
    const [documentLoading, setDocumentLoading] = useState(false);
    const [zipcode, setZipcode] = useState('');
    const [huisnummer, setHuisnummer] = useState('');
    const [documents, setDocuments] = useState<Document[]>([]);
    // Initialize with schema when component mounts
    const setInitialCharacteristics = () => {
        const initialCharacteristics: Record<string, {
            answer: string;
            explanation: string;
            confidence: number | null;
            isEdited: boolean;
        }> = {};
        Object.keys(typedSchema).forEach(key => {
            initialCharacteristics[key] = {
                answer: '',
                explanation: '',
                confidence: null,
                isEdited: false
            };
        });
        setEditableCharacteristics(initialCharacteristics);
    }
    useEffect(() => {
        setInitialCharacteristics();
    }, []);

    const [autoUpdateMeldings, setAutoUpdateMeldings] = useState(true);
    const [autoUpdateDocuments, setAutoUpdateDocuments] = useState(true);

    // When new characteristics are estimated, update the editable characteristics
    useEffect(() => {
        if (estimatedCharacteristicsReport) {
            const newCharacteristics: Record<string, {
                answer: string;
                explanation: string;
                confidence: number | null;
                isEdited: boolean;
            }> = {};
            
            // Convert the received characteristics and merge with schema
            Object.keys(typedSchema).forEach(key => {
                if (estimatedCharacteristicsReport.characteristics[key]) {
                    // If the characteristic exists in the report
                    const char = estimatedCharacteristicsReport.characteristics[key];
                    newCharacteristics[key] = {
                        answer: char.answer,
                        explanation: char.explanation,
                        confidence: char.confidence,
                        isEdited: false
                    };
                } else {
                    // If the characteristic doesn't exist in the report
                    newCharacteristics[key] = {
                        answer: '',
                        explanation: '',
                        confidence: null,
                        isEdited: false
                    };
                }
            });
            
            setEditableCharacteristics(newCharacteristics);
        }
    }, [estimatedCharacteristicsReport]);

  

    // Add function to calculate the aggregated confidence
    const getAggregatedConfidence = () => {
        const confidenceValues = Object.values(editableCharacteristics)
            .filter(char => char.confidence !== null)
            .map(char => char.confidence as number);
        
        if (confidenceValues.length === 0) return null;
        
        return confidenceValues.reduce((sum, confidence) => sum + confidence, 0) / confidenceValues.length;
    };

    // Render the aggregated confidence display
    const renderAggregatedConfidence = () => {
        const aggregatedConfidence = getAggregatedConfidence();
        
        if (aggregatedConfidence === null) return null;
        
        return (
            <div className="flex flex-col items-start mb-2 gap-2 w-fit">
                <span className="text-lg">Average Confidence</span>
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-lg font-medium ${
                    aggregatedConfidence >= 0.7 ? 'bg-green-100 text-green-800' :
                    aggregatedConfidence >= 0.4 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                }`}>
                    {Math.round(aggregatedConfidence * 100)}%
                </span>
            </div>
        );
    };

    const renderCharacteristicsEstimatedCount = () => {
        const estimatedCharacteristicsCount = Object.values(editableCharacteristics).filter(char => char.answer !== '').length;
        const totalCharacteristics = Object.keys(typedSchema).length;
        const percentage = totalCharacteristics > 0 ? Math.round((estimatedCharacteristicsCount / totalCharacteristics) * 100) : 0;
        
        return (
            <div className="flex flex-col items-start mb-2 gap-2 w-fit">
                <span className="text-lg">Characteristics Estimated</span>
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-lg font-medium ${
                    percentage >= 70 ? 'bg-green-100 text-green-800' :
                    percentage >= 40 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                }`}>
                    {estimatedCharacteristicsCount} / {totalCharacteristics} ({percentage}%)
                </span>
            </div>
        );
    };


    const getDocumentsFromCharacteristic = () => {
        setDocumentLoading(true);
        setDocuments([]);
        fetch(`${BACKEND_URL}/vnog/get_documents_from_characteristic/`, {
            method: 'POST',
            body: new URLSearchParams({
                'characteristics': JSON.stringify(editableCharacteristics)
            }),
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'X-CSRFToken': Cookies.get('csrftoken') || '',
            },
            credentials: 'include',
        })
            .then(response => response.json())
            .then(data => {
                console.log('Data:', data);
                setDocuments(data.documents);
            })
            .catch(error => console.log('Error:', error))
            .finally(() => setDocumentLoading(false));
    }

    const updateEstimations = () => {
        if (zipcode && huisnummer) {
            console.log('Update Estimations');
            getEstimatedCharacteristicsFromAddress();
        }else{
            toast.error('Please enter a zipcode and huisnummer');
        }
    }


    useEffect(() => {
        console.log('meldings', selectedMeldings);
        console.log('editableCharacteristics', editableCharacteristics);
        if (autoUpdateMeldings && selectedMeldings.length > 0) {
            updateEstimations();
        }
    }, [selectedMeldings, autoUpdateMeldings]);

    useEffect(() => {
        const hasEditedCharacteristics = Object.values(editableCharacteristics).some(char => char.isEdited || char.confidence !== null);
        if (autoUpdateDocuments && hasEditedCharacteristics) {
            console.log('autoUpdateDocuments', autoUpdateDocuments);
            getDocumentsFromCharacteristic();
        }
    }, [editableCharacteristics, autoUpdateDocuments]);

   

    const getEstimatedCharacteristicsFromAddress = () => {
        if (!zipcode || !huisnummer) {
            toast.error('Please enter a zipcode and huisnummer');
            return;
        }
        setEstimationLoading(true);
        fetch(`${BACKEND_URL}/vnog/estimate_characteristics/`, {
            method: 'POST',
            body: new URLSearchParams({
                'zipcode': zipcode,
                'huisnummer': huisnummer,
                'meldingsclassificaties': selectedMeldings.join('\n')
            }),
            headers: { 
                'X-CSRFToken': Cookies.get('csrftoken') || '',
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            credentials: 'include',
        })  
        .then(response => response.json())
        .then(data => {
            console.log('Data:', data);
            if (data.error) {
               toast.error(data.error);
            } else {
                setEstimatedCharacteristicsReport(data);
            }
        })
        .catch(error => console.log('Error:', error.error))
        .finally(() => setEstimationLoading(false));
    }
  
    return (
        <div className="min-h-screen bg-gray-50 p-2">
            <ToastContainer />
            <div className="w-full flex flex-row justify-between h-12">
                <div className="flex flex-col items-start">
                    <img src="/vnog.svg" alt="VNOG Logo" className="h-12 w-auto" />
                    <p className="text-xs text-gray-500 pt-2">
                        Proof of Concept - Virtuele Assistent Bevelvoerder
                    </p>
                </div>
                <div>
                    <button className="bg-blue-500 text-white px-4 py-2 rounded-md" onClick={() => {
                        setInitialCharacteristics();
                        setEstimatedCharacteristicsReport(null);
                        setZipcode('');
                        setHuisnummer('');
                        setDocuments([]);
                        setSelectedMeldings([]);
                    }}>
                        Clear All
                    </button>
                </div>
            </div>
            <div className="flex flex-row justify-between pt-8 gap-2">
                <div className="flex flex-col w-1/2 gap-2">
                    <div className="flex flex-row gap-24 h-[50vh] lg:h-[45vh] justify-start bg-white rounded-lg shadow-md p-4">
                        <div>
                            <EstimateCharacteristicsFromAddress getEstimatedCharacteristicsFromAddress={getEstimatedCharacteristicsFromAddress} loading={estimationLoading} zipcode={zipcode} huisnummer={huisnummer} setZipcode={setZipcode} setHuisnummer={setHuisnummer} />
                        </div>
                        <div className="w-fit pt-[62px]">
                            {renderCharacteristicsEstimatedCount()}
                            {renderAggregatedConfidence()}
                        </div>
                    </div>
                    <div className="flex flex-col h-[60vh] lg:h-[70vh]">  
                        <Characteristics 
                            editableCharacteristics={editableCharacteristics} 
                            setEditableCharacteristics={setEditableCharacteristics} 
                            typedSchema={typedSchema}
                        />
                    </div>
                </div>
                <div className="flex flex-col w-1/2 gap-2">
                    <div className="flex flex-col h-[50vh] lg:h-[45vh] bg-white rounded-lg shadow-md p-4">
                        <Meldings selectedMeldings={selectedMeldings} setSelectedMeldings={setSelectedMeldings} autoUpdate={autoUpdateMeldings} setAutoUpdate={setAutoUpdateMeldings} />
                    </div>
                    <div className="flex flex-col h-[60vh] lg:h-[70vh]">
                        <Documents loading={documentLoading} documents={documents} getDocumentsFromCharacteristic={getDocumentsFromCharacteristic} autoUpdate={autoUpdateDocuments} setAutoUpdate={setAutoUpdateDocuments} />
                    </div>
                </div>
            </div>
            <div>
                {estimatedCharacteristicsReport && (
                    <div className="bg-white mt-8 bg-white rounded-lg shadow-md p-4">
                        <div className="px-4 py-5 sm:px-6">
                            <h3 className="text-lg font-medium text-gray-900">Report</h3>
                        </div>
                        <div className="border-t border-gray-200">
                            <pre className="p-4 text-sm text-gray-700 whitespace-pre-wrap">
                                {estimatedCharacteristicsReport.report}
                            </pre>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Home;